import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Empty, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import {
  ApiError,
  api,
  type AdminDocumentRow,
  type AdminFormField,
  type DocumentSignatory,
  type Merchant,
  type MerchantOnboarding,
  type OnboardingRow,
} from './api.js';
import { LogoCropModal } from './LogoCropModal.js';

/**
 * Legal documents, from the operator's side: set your own counter-signature,
 * see every client's issued and signed documents, open any of them, and correct
 * the identity fields (EIN and the rest) no upstream system yields.
 */
export function AdminDocumentsView() {
  const [viewing, setViewing] = useState<AdminDocumentRow | null>(null);
  if (viewing) return <DocViewer row={viewing} onBack={() => setViewing(null)} />;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <OnboardingReviewPanel />
      <RiskReviewPanel />
      <MandatoryDocsPanel />
      <SignatoryPanel />
      <AllDocuments onOpen={setViewing} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Due-diligence risk review: the operator completes the assessment (Sections
// D-H) that the merchant does not fill or see.
// ---------------------------------------------------------------------------

const DD_KEY = 'merchant-due-diligence-risk-assessment';

function RiskReviewPanel() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Merchant[]>([]);
  const [picked, setPicked] = useState<Merchant | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.merchants({ search: search.trim() });
      setResults(r.data.slice(0, 12));
    } catch {
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  if (picked) return <RiskReview merchant={picked} onBack={() => setPicked(null)} />;

  return (
    <Panel title="Due diligence и оценка на риска">
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--wc-muted)' }}>
        Търговецът попълва бизнес профила; вие попълвате оценката на риска и решението. Намерете търговец:
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="Име, имейл или ID"
          style={{ flex: '1 1 260px', padding: '8px 10px', border: '1px solid var(--wc-border)', borderRadius: 8, fontSize: 13.5 }}
        />
        <button type="button" className="wc-btn wc-btn-sm" onClick={run} disabled={busy}>
          {busy ? 'Търсене…' : 'Търси'}
        </button>
      </div>
      {results.length ? (
        <div style={{ display: 'grid', gap: 6, marginTop: 12 }}>
          {results.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setPicked(m)}
              style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid var(--wc-border)', borderRadius: 8, background: 'var(--wc-bg,#fff)', cursor: 'pointer' }}
            >
              <b>{m.name}</b> <span style={{ color: 'var(--wc-faint)', fontSize: 12 }}>{m.id}</span>
            </button>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function RiskReview({ merchant, onBack }: { merchant: Merchant; onBack: () => void }) {
  const [data, setData] = useState<{
    merchantFields: AdminFormField[];
    reviewerFields: AdminFormField[];
    merchant: Record<string, string>;
    reviewer: Record<string, string>;
    state?: { state?: string; number?: string | null };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [showDoc, setShowDoc] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [issuedNo, setIssuedNo] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .documentForm(merchant.id, DD_KEY)
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Неуспешно зареждане.'));
  }, [merchant.id]);
  useEffect(() => load(), [load]);

  const preview = async () => {
    setShowDoc(true);
    setHtml(null);
    try {
      setHtml(frameDocument(await api.adminDocumentHtmlByKey(merchant.id, DD_KEY)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Документът не можа да се зареди.');
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    setError(null);
    try {
      const res = await api.issueReviewedDocument(merchant.id, DD_KEY);
      setIssuedNo(res.number);
      if (showDoc) await preview();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Неуспешно издаване.');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <Panel
      title={`Оценка на риска - ${merchant.name}`}
      actions={
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
          ← Назад
        </button>
      }
    >
      {error ? <Note tone="danger">{error}</Note> : null}
      {!data ? (
        <Spinner label="Зареждане…" />
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
              Бизнес профил (попълнен от търговеца)
            </div>
            {Object.keys(data.merchant).length ? (
              <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))' }}>
                {data.merchantFields
                  .filter((f) => data.merchant[f.token])
                  .map((f) => (
                    <div key={f.token} style={{ fontSize: 12.5 }}>
                      <span style={{ color: 'var(--wc-faint)' }}>{f.label}: </span>
                      <b>{labelFor(f, data.merchant[f.token])}</b>
                    </div>
                  ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--wc-faint)' }}>Търговецът още не е попълнил профила.</p>
            )}
          </div>
          <AdminFormEditor
            fields={data.reviewerFields}
            values={data.reviewer}
            onSave={async (values) => {
              const res = await api.saveReviewerForm(merchant.id, DD_KEY, values);
              return res.values;
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={preview}>
              Преглед на документа
            </button>
            <button type="button" className="wc-btn wc-btn-sm" onClick={finalize} disabled={finalizing}>
              {finalizing ? 'Издаване…' : 'Издай и подпечатай оценката'}
            </button>
            {issuedNo ? (
              <span style={{ fontSize: 12.5, color: '#1c7a43' }}>Издадено и подпечатано: {issuedNo}</span>
            ) : data.state?.state === 'accepted' && data.state.number ? (
              <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>Издадено: {data.state.number}</span>
            ) : null}
          </div>
          {showDoc ? (
            html ? (
              <iframe
                title="Due diligence"
                srcDoc={html}
                style={{ width: '100%', minHeight: 600, border: '1px solid var(--wc-border)', borderRadius: 10, background: '#fff' }}
              />
            ) : (
              <Spinner label="Зареждане на документа…" />
            )
          ) : null}
        </div>
      )}
    </Panel>
  );
}

/** The label of a choice value, or the raw value for text fields. */
function labelFor(field: AdminFormField, value: string): string {
  if (field.type === 'radio' || field.type === 'select') {
    return field.options?.find((o) => o.key === value)?.label ?? value;
  }
  return value;
}

/** A generic editor for admin reviewer fields, grouped by section. */
function AdminFormEditor({
  fields,
  values,
  onSave,
}: {
  fields: AdminFormField[];
  values: Record<string, string>;
  onSave: (values: Record<string, string>) => Promise<Record<string, string>>;
}) {
  const [data, setData] = useState<Record<string, string>>(values);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (t: string, v: string) => {
    setData((d) => ({ ...d, [t]: v }));
    setSaved(false);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await onSave(data));
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Неуспешно записване.');
    } finally {
      setBusy(false);
    }
  };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--wc-border)', borderRadius: 8, fontSize: 13.5, background: 'var(--wc-bg,#fff)', color: 'var(--wc-ink)' };
  const groups: Array<{ section: string; fields: AdminFormField[] }> = [];
  for (const f of fields) {
    const s = f.section ?? '';
    let g = groups.find((x) => x.section === s);
    if (!g) {
      g = { section: s, fields: [] };
      groups.push(g);
    }
    g.fields.push(f);
  }
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em' }}>
        Оценка и решение (попълва се от вас)
      </div>
      {groups.map((g) => (
        <div key={g.section}>
          {g.section ? (
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--wc-muted)', marginBottom: 6 }}>{g.section}</div>
          ) : null}
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))' }}>
            {g.fields.map((f) => (
              <div key={f.token} style={{ gridColumn: f.span === 2 ? '1 / -1' : undefined }}>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--wc-muted)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={data[f.token] ?? ''} onChange={(e) => set(f.token, e.target.value)} />
                ) : f.type === 'radio' || f.type === 'select' ? (
                  <select style={inp} value={data[f.token] ?? ''} onChange={(e) => set(f.token, e.target.value)}>
                    <option value="">-</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input style={inp} type={f.type === 'date' ? 'date' : 'text'} value={data[f.token] ?? ''} onChange={(e) => set(f.token, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {error ? <Note tone="danger">{error}</Note> : null}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button type="button" className="wc-btn wc-btn-sm" onClick={save} disabled={busy}>
          {busy ? 'Записване…' : 'Запиши оценката'}
        </button>
        {saved ? <span style={{ fontSize: 12.5, color: '#1c7a43' }}>Записано</span> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Which documents are mandatory for the gate

function MandatoryDocsPanel() {
  const [all, setAll] = useState<Array<{ key: string; title: string }>>([]);
  const [mandatory, setMandatory] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [enforced, setEnforced] = useState<boolean | null>(null);
  const [confirmOn, setConfirmOn] = useState(false);

  useEffect(() => {
    api
      .mandatoryDocuments()
      .then((r) => {
        setAll(r.all);
        setMandatory(r.mandatory);
        setLoaded(true);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    api
      .onboardingEnforcement()
      .then((r) => setEnforced(r.enabled))
      .catch(() => setEnforced(null));
  }, []);

  const toggleEnforcement = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api.setOnboardingEnforcement(next);
      setEnforced(next);
      setConfirmOn(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return error ? <Note tone="danger">{error}</Note> : null;

  const toggle = (key: string) => {
    setMandatory((m) => (m.includes(key) ? m.filter((k) => k !== key) : [...m, key]));
    setSaved(false);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveMandatoryDocuments(mandatory);
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Задължителни документи и достъп (gate)">
      {enforced !== null ? (
        <div
          style={{
            padding: 12,
            border: '1px solid var(--wc-border)',
            borderRadius: 8,
            marginBottom: 12,
            background: enforced ? 'var(--wc-warn-soft, #fdf3e3)' : 'transparent',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Gate: {enforced ? 'ВКЛЮЧЕН' : 'изключен'}
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--wc-muted)' }}>
            Когато е включен, ВСЕКИ търговец (включително твоят собствен акаунт) е заключен, докато не
            попълни данните, качи документ, получи одобрение и подпише задължителните документи.
          </p>
          {enforced ? (
            <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => toggleEnforcement(false)} disabled={busy}>
              Изключи gate
            </button>
          ) : confirmOn ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="wc-btn wc-btn-danger wc-btn-sm" onClick={() => toggleEnforcement(true)} disabled={busy}>
                Да, включи (заключва всички незавършили)
              </button>
              <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setConfirmOn(false)} disabled={busy}>
                Отказ
              </button>
            </div>
          ) : (
            <button type="button" className="wc-btn wc-btn-sm" onClick={() => setConfirmOn(true)} disabled={busy}>
              Включи gate
            </button>
          )}
        </div>
      ) : null}
      <Note>Отметни кои документи търговецът е длъжен да подпише, преди да ползва платформата.</Note>
      <div style={{ display: 'grid', gap: 8, margin: '10px 0' }}>
        {all.map((d) => (
          <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={mandatory.includes(d.key)} onChange={() => toggle(d.key)} />
            <span>{d.title}</span>
          </label>
        ))}
      </div>
      {error ? <Note tone="danger">{error}</Note> : null}
      {saved ? <Note tone="ok">Записано.</Note> : null}
      <div style={{ marginTop: 8 }}>
        <button type="button" className="wc-btn" onClick={save} disabled={busy}>
          {busy ? 'Записване…' : 'Запази'}
        </button>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Onboarding / KYC review

function OnboardingReviewPanel() {
  const [rows, setRows] = useState<OnboardingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<OnboardingRow | null>(null);

  const load = useCallback(() => {
    setRows(null);
    api
      .onboardingQueue()
      .then((r) => setRows(r.merchants))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);
  useEffect(() => load(), [load]);

  if (error) return <Note tone="danger">{error}</Note>;
  if (!rows) return <Spinner label="Зареждане на заявките…" />;

  const badge = (status: string) =>
    status === 'SUBMITTED' ? <Badge tone="warn">За преглед</Badge>
    : status === 'APPROVED' ? <Badge tone="ok">Одобрен</Badge>
    : status === 'REJECTED' ? <Badge tone="fail">Върнат</Badge>
    : <Badge tone="neutral">{status}</Badge>;

  return (
    <Panel title={`KYC одобрения${rows.filter((r) => r.status === 'SUBMITTED').length ? ` (${rows.filter((r) => r.status === 'SUBMITTED').length} чакат)` : ''}`}>
      {rows.length === 0 ? (
        <Empty>Няма подадени заявки за проверка.</Empty>
      ) : (
        <TableWrap>
          <table className="wc-table">
            <thead>
              <tr><th>Търговец</th><th>Държава</th><th>Статус</th><th>Документи</th><th>Подадено</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><div style={{ fontWeight: 600 }}>{r.name}</div><div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{r.email}</div></td>
                  <td>{r.country}</td>
                  <td>{badge(r.status)}</td>
                  <td>{r.document_count}</td>
                  <td style={{ fontSize: 12 }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '-'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setOpen(r)}>Прегледай</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {open ? (
        <OnboardingReview
          row={open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            load();
          }}
        />
      ) : null}
    </Panel>
  );
}

function OnboardingReview({ row, onClose, onDone }: { row: OnboardingRow; onClose: () => void; onDone: () => void }) {
  const [detail, setDetail] = useState<MerchantOnboarding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    api
      .merchantOnboarding(row.id)
      .then(setDetail)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [row.id]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.approveOnboarding(row.id);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  const reject = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.rejectOnboarding(row.id, reason.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--wc-border)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 600 }}>Преглед - {row.name}</div>
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onClose}>Затвори</button>
      </div>
      {error ? <div style={{ marginTop: 8 }}><Note tone="danger">{error}</Note></div> : null}
      {!detail ? (
        <Spinner label="Зареждане…" />
      ) : (
        <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--wc-muted)', marginBottom: 4 }}>ВЪВЕДЕНИ ДАННИ</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>
              <li>Рег. номер: <b>{detail.data.company_registration_no || '-'}</b></li>
              <li>EIN / Tax ID: <b>{detail.data.tax_id || '-'}</b></li>
              <li>VAT: <b>{detail.data.vat || '-'}</b></li>
              <li>DBA: <b>{detail.data.dba || '-'}</b></li>
              <li>Длъжност: <b>{detail.data.representative_title || '-'}</b></li>
              <li>Роля: <b>{detail.data.representative_role || '-'}</b></li>
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--wc-muted)', marginBottom: 4 }}>ДОКУМЕНТИ ({detail.documents.length})</div>
            {detail.documents.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--wc-faint)' }}>Няма прикачени документи.</p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {detail.documents.map((d) => (
                  <a
                    key={d.id}
                    href={api.kycDocumentUrl(row.id, d.id)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13, color: 'var(--wc-blue)', textDecoration: 'none' }}
                  >
                    {d.filename} <span style={{ color: 'var(--wc-muted)' }}>({d.kind} · {(d.size / 1024).toFixed(0)} KB) ↗</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {rejecting ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <input className="wc-input" placeholder="Причина за връщане (виждат я)" value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="wc-btn wc-btn-danger wc-btn-sm" onClick={reject} disabled={busy || reason.trim().length < 3}>Върни</button>
                <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setRejecting(false)} disabled={busy}>Отказ</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="wc-btn" onClick={approve} disabled={busy}>{busy ? 'Обработка…' : 'Одобри'}</button>
              <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setRejecting(true)} disabled={busy}>Върни за корекция</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The operator's counter-signature

function SignatoryPanel() {
  const [state, setState] = useState<DocumentSignatory | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  useEffect(() => {
    let live = true;
    api
      .documentSignatory()
      .then((s) => live && setState(s))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);

  if (!state) return error ? <Note tone="danger">{error}</Note> : <Spinner label="Зареждане…" />;

  const set = (patch: Partial<DocumentSignatory>) => {
    setState({ ...state, ...patch });
    setSaved(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveDocumentSignatory(state);
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Вашият подпис (Wyoming Cloud Solutions LLC)">
      <div style={{ display: 'grid', gap: 12 }}>
        <Note>
          Това е вашият насрещен подпис в блока „WYOMING CLOUD SOLUTIONS LLC - ACCEPTANCE“. Задава се
          веднъж и се полага автоматично на всеки договор в момента, в който търговецът подпише - така
          готовият договор носи и двата подписа и влиза в криптографския печат. Задайте го{' '}
          <b>преди</b> клиентите да подписват.
        </Note>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Име (както се подписвате)">
            <input className="wc-input" value={state.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Длъжност">
            <input className="wc-input" value={state.title} maxLength={80} placeholder="напр. Owner, Managing Member" onChange={(e) => set({ title: e.target.value })} />
          </Field>
        </div>

        <Field label="Подпис (нарисувайте или качете изображение)">
          <SignatureCapture value={state.signature} onChange={(sig) => set({ signature: sig })} onUpload={setCropFile} />
        </Field>

        {error ? <Note tone="danger">{error}</Note> : null}
        {saved ? <Note tone="ok">Записано. Прилага се при следващите подписвания.</Note> : null}
        <div>
          <button type="button" className="wc-btn" onClick={save} disabled={busy}>
            {busy ? 'Записване…' : 'Запази подписа'}
          </button>
        </div>
      </div>

      {cropFile ? (
        <LogoCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onSave={(base64) => {
            set({ signature: `data:image/png;base64,${base64}` });
            setCropFile(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

/**
 * Return the drawn signature as a PNG data URL cropped tight to the ink, so a
 * small signature on a big canvas is not scaled down to nothing when it lands on
 * the document. Falls back to the whole canvas if nothing was drawn.
 */
function trimmedSignature(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.toDataURL('image/png');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width, minY = height, maxX = 0, maxY = 0, found = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 12) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return canvas.toDataURL('image/png');
  const pad = 10;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d')?.drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
  return out.toDataURL('image/png');
}

/** Draw a signature, or hand a chosen file up for cropping. Shows the current one. */
function SignatureCapture({
  value,
  onChange,
  onUpload,
}: {
  value: string;
  onChange: (dataUrl: string) => void;
  onUpload: (file: File) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = point(e);
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#1e46a8';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) onChange(trimmedSignature(canvasRef.current!));
  };
  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange('');
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {value ? (
        <div style={{ border: '1px solid var(--wc-border)', borderRadius: 8, padding: 10, background: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--wc-muted)' }}>Текущ подпис:</span>
          <img src={value} alt="подпис" style={{ maxHeight: 46, maxWidth: 260, objectFit: 'contain' }} />
        </div>
      ) : null}
      <div style={{ border: '1px solid var(--wc-border)', borderRadius: 8, background: '#fff', padding: 8 }}>
        <canvas
          ref={canvasRef}
          width={640}
          height={150}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          style={{ width: '100%', height: 150, touchAction: 'none', cursor: 'crosshair', display: 'block', borderBottom: '1px solid var(--wc-border)' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--wc-faint)' }}>Нарисувайте с мишка/тъчпад, или качете PNG/JPG за изрязване.</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <label className="wc-btn wc-btn-secondary wc-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
              Качи изображение
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUpload(f);
                  e.currentTarget.value = '';
                }}
              />
            </label>
            <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={clear}>
              Изчисти
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Every client's documents

function AllDocuments({ onOpen }: { onOpen: (row: AdminDocumentRow) => void }) {
  const [rows, setRows] = useState<AdminDocumentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminDocumentRow | null>(null);

  const load = useCallback(() => {
    setRows(null);
    api
      .adminDocuments()
      .then((r) => setRows(r.documents))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);
  useEffect(() => load(), [load]);

  if (error) return <Note tone="danger">{error}</Note>;
  if (!rows) return <Spinner label="Зареждане на документите…" />;

  const badge = (status: string) =>
    status === 'ACCEPTED' ? <Badge tone="ok">Подписан</Badge>
    : status === 'DECLINED' ? <Badge tone="fail">Отказан</Badge>
    : status === 'ISSUED' ? <Badge tone="warn">Издаден</Badge>
    : <Badge tone="neutral">{status}</Badge>;

  return (
    <Panel title="Документи на клиентите">
      {rows.length === 0 ? (
        <Empty>Все още няма издадени документи.</Empty>
      ) : (
        <TableWrap>
          <table className="wc-table">
            <thead>
              <tr>
                <th>Търговец</th>
                <th>Документ</th>
                <th>Номер</th>
                <th>Статус</th>
                <th>Подписан на</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.merchant_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{r.merchant_email}</div>
                  </td>
                  <td>{r.title}</td>
                  <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>{r.number}</td>
                  <td>{badge(r.status)}</td>
                  <td style={{ fontSize: 12 }}>{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : '-'}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => onOpen(r)}>
                      Отвори
                    </button>{' '}
                    <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setEditing(r)}>
                      Данни
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {editing ? (
        <IdentityOverride
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

/** Override a merchant's document identity fields (EIN, company no, DBA, title). */
function IdentityOverride({
  row,
  onClose,
  onSaved,
}: {
  row: AdminDocumentRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [taxId, setTaxId] = useState('');
  const [reg, setReg] = useState('');
  const [dba, setDba] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveMerchantDocumentDetails(row.merchant_id, {
        tax_id: taxId,
        company_registration_no: reg,
        dba,
        representative_title: title,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--wc-border)', borderRadius: 10, padding: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Данни за документите - {row.merchant_name}</div>
      <Note>
        EIN/данъчен номер, регистрационен номер, търговско име и длъжност - попълват се на бъдещи
        издавания на документи за този търговец. Празно поле изчиства стойността.
      </Note>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginTop: 10 }}>
        <Field label="EIN / Tax ID / VAT ID">
          <input className="wc-input" value={taxId} maxLength={64} onChange={(e) => setTaxId(e.target.value)} />
        </Field>
        <Field label="Регистрационен номер">
          <input className="wc-input" value={reg} maxLength={64} onChange={(e) => setReg(e.target.value)} />
        </Field>
        <Field label="DBA / търговско име">
          <input className="wc-input" value={dba} maxLength={120} onChange={(e) => setDba(e.target.value)} />
        </Field>
        <Field label="Длъжност на представителя">
          <input className="wc-input" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      </div>
      {error ? <div style={{ marginTop: 8 }}><Note tone="danger">{error}</Note></div> : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" className="wc-btn" onClick={save} disabled={busy}>
          {busy ? 'Записване…' : 'Запази'}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onClose} disabled={busy}>
          Затвори
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Viewing one document

function frameDocument(island: string): string {
  const base = `${window.location.origin}/documents/runtime`;
  const body = island.split('__RUNTIME_BASE__').join(base);
  return (
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<script src="${base}/react.production.min.js"></script>` +
    `<script src="${base}/react-dom.production.min.js"></script>` +
    `<script src="${base}/support.js"></script>` +
    `</head><body>${body}</body></html>`
  );
}

function DocViewer({ row, onBack }: { row: AdminDocumentRow; onBack: () => void }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let live = true;
    api
      .adminDocumentHtml(row.id)
      .then((markup) => live && setHtml(frameDocument(markup)))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [row.id]);

  const print = () => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 'calc(100vh - 210px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
          ← Назад
        </button>
        <div style={{ flex: '1 1 auto', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
          {row.merchant_name} - {row.title} ({row.number})
        </div>
        <button type="button" className="wc-btn wc-btn-sm" onClick={print} disabled={!html}>
          Печат / PDF
        </button>
      </div>
      {error ? (
        <Note tone="danger">{error}</Note>
      ) : !html ? (
        <div style={{ display: 'grid', placeItems: 'center', flex: '1 1 auto', minHeight: 320 }}>
          <Spinner label="Подготовка…" />
        </div>
      ) : (
        <iframe
          ref={frameRef}
          title={row.title}
          srcDoc={html}
          style={{ flex: '1 1 auto', width: '100%', minHeight: 'calc(100vh - 260px)', border: '1px solid var(--wc-border)', borderRadius: 10, background: '#fff' }}
        />
      )}
    </div>
  );
}
